import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Music2 } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShowEpisodesGroup } from "./ShowEpisodesGroup";

const empty = { show_id: "", title: "", summary: "", audio_url: "", image_url: "", published_at: "", duration: "" };
const selectCls = "mt-1.5 w-full h-10 rounded-md bg-white/5 border border-white/10 px-3 text-sm";

export default function EpisodesAdmin() {
  const [shows, setShows] = useState([]);
  const [filter, setFilter] = useState("");
  const [activity, setActivity] = useState([]);
  const [version, setVersion] = useState(0);
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState(params.get("new") ? { ...empty } : null);
  const [saving, setSaving] = useState(false);
  const [tiktok, setTiktok] = useState(null);

  const addTiktoks = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/admin/episodes/tiktok", tiktok);
      toast.success(`${data.added} clip${data.added === 1 ? "" : "s"} added${data.failed.length ? ` · ${data.failed.length} link(s) failed` : ""}`);
      setTiktok(null); load();
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setSaving(false); }
  };

  useEffect(() => { api.get("/shows", { params: { include_inactive: true } }).then((r) => setShows(r.data)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: run once on mount
  const load = () => api.get("/admin/shows/activity").then((r) => { setActivity(r.data); setVersion((v) => v + 1); }).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: run once on mount
  const visible = filter ? activity.filter((s) => s.id === filter) : activity;

  const closeDialog = () => { setEditing(null); if (params.get("new")) setParams({}); };
  const set = (k, v) => setEditing((s) => ({ ...s, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { id, ...body } = editing;
      const payload = Object.fromEntries(Object.entries(body).filter(([k]) => k in empty));
      if (payload.published_at) payload.published_at = new Date(payload.published_at).toISOString(); else delete payload.published_at;
      if (id) { delete payload.show_id; await api.put(`/admin/episodes/${id}`, payload); }
      else await api.post("/admin/episodes", payload);
      toast.success(id ? "Episode updated" : "Episode published");
      closeDialog(); load();
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setSaving(false); }
  };

  const openEdit = (ep) => setEditing({ ...ep, published_at: ep.published_at ? ep.published_at.slice(0, 16) : "" });

  return (
    <div data-testid="admin-episodes">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div><p className="eyebrow mb-2">Content</p><h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Episodes</h1></div>
        <div className="flex items-center gap-3">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-10 rounded-full bg-white/5 border border-white/10 px-4 text-sm" data-testid="episodes-show-filter">
            <option value="">All shows</option>
            {shows.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {shows.some((s) => s.kind === "tiktok") && <Button onClick={() => setTiktok({ show_id: shows.find((s) => s.kind === "tiktok")?.id || "", urls: "" })} variant="outline" className="rounded-full border-white/15 bg-transparent hover:bg-white/10" data-testid="add-tiktoks-button"><Music2 size={16} className="mr-1 text-cyan-300" /> Add TikToks</Button>}
          <Button onClick={() => setEditing({ ...empty, show_id: filter })} className="rounded-full bg-sky-400 text-[#070F1E] font-bold hover:bg-sky-300" data-testid="add-episode-button"><Plus size={16} className="mr-1" /> New episode</Button>
        </div>
      </div>

      <Dialog open={!!tiktok} onOpenChange={(o) => !o && setTiktok(null)}>
        <DialogContent className="card-surface max-w-xl" data-testid="tiktok-form-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Add TikToks</DialogTitle></DialogHeader>
          {tiktok && (
            <form onSubmit={addTiktoks} className="space-y-4 mt-2">
              <p className="text-xs text-slate-500">Paste one TikTok video link per line. Title and thumbnail are pulled automatically; clips are listed newest first.</p>
              <div><Label>Show</Label>
                <select required value={tiktok.show_id} onChange={(e) => setTiktok((t) => ({ ...t, show_id: e.target.value }))} className={selectCls} data-testid="tiktok-show-select">
                  {shows.filter((s) => s.kind === "tiktok").map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div><Label>Video links</Label><Textarea required rows={6} value={tiktok.urls} onChange={(e) => setTiktok((t) => ({ ...t, urls: e.target.value }))} placeholder={"https://www.tiktok.com/@underdogpodcasts/video/7300000000000000000\nhttps://www.tiktok.com/@underdogpodcasts/video/…"} className="mt-1.5 bg-white/5 border-white/10 font-mono text-xs" data-testid="tiktok-urls-input" /></div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="ghost" onClick={() => setTiktok(null)} data-testid="tiktok-form-cancel">Cancel</Button>
                <Button type="submit" disabled={saving} className="rounded-full bg-sky-400 text-[#070F1E] font-bold hover:bg-sky-300" data-testid="tiktok-form-submit">{saving ? "Fetching…" : "Add clips"}</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
      <p className="text-xs text-slate-500 mb-3" data-testid="episodes-help">Shows are ordered by most recently updated. Click <span className="font-mono">+</span> to open a show's episodes, then click any title to edit it inline.</p>
      <div className="space-y-2" data-testid="admin-episodes-list">
        {visible.map((s, i) => <ShowEpisodesGroup key={s.id} show={s} index={i} version={version} onEdit={openEdit} onChanged={load} />)}
        {activity.length > 0 && visible.length === 0 && <p className="text-slate-400 text-sm">No matching show.</p>}
        {activity.length === 0 && <p className="text-slate-400 text-sm">No shows yet.</p>}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="card-surface max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="episode-form-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing?.id ? "Edit episode" : "Post a new episode"}</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-4 mt-2">
              {!editing.id && (
                <div><Label>Show *</Label>
                  <select required value={editing.show_id} onChange={(e) => set("show_id", e.target.value)} className={selectCls} data-testid="episode-show-select">
                    <option value="">Select a show…</option>
                    {shows.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              )}
              <div><Label>Episode title *</Label><Input required value={editing.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" data-testid="episode-title-input" /></div>
              {editing.video_id
                ? <p className="text-xs text-slate-500" data-testid="episode-video-note">{editing.source === "tiktok" ? "TikTok" : "YouTube"} clip · video ID <span className="font-mono">{editing.video_id}</span>. Your title and description edits are kept on future syncs.</p>
                : <div><Label>Audio URL (MP3) *</Label><Input required value={editing.audio_url} onChange={(e) => set("audio_url", e.target.value)} placeholder="https://…/episode.mp3" className="mt-1.5 bg-white/5 border-white/10" data-testid="episode-audio-input" /></div>}
              <div><Label>Episode description</Label><Textarea value={editing.summary} onChange={(e) => set("summary", e.target.value)} rows={5} placeholder="Full show notes — the first few lines appear on cards, the rest behind “Read more”" className="mt-1.5 bg-white/5 border-white/10" data-testid="episode-summary-input" /></div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="sm:col-span-1"><Label>Artwork URL</Label><Input value={editing.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="Defaults to show cover" className="mt-1.5 bg-white/5 border-white/10" data-testid="episode-image-input" /></div>
                <div><Label>Publish date</Label><Input type="datetime-local" value={editing.published_at} onChange={(e) => set("published_at", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" data-testid="episode-date-input" /></div>
                {!editing.video_id && <div><Label>Duration</Label><Input value={editing.duration} onChange={(e) => set("duration", e.target.value)} placeholder="45:00" className="mt-1.5 bg-white/5 border-white/10" data-testid="episode-duration-input" /></div>}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="ghost" onClick={closeDialog} data-testid="episode-form-cancel">Cancel</Button>
                <Button type="submit" disabled={saving} className="rounded-full bg-sky-400 text-[#070F1E] font-bold hover:bg-sky-300" data-testid="episode-form-submit">{saving ? "Saving…" : editing.id ? "Save changes" : "Publish episode"}</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
